import { Paper, Flex, Text, Badge, Divider, Button, Loader, Center } from '@mantine/core';
import PaymentHistoryItem from './PaymentHistoryItem';
import { useMemo, useState, useEffect } from 'react';
import axios from 'axios';

interface PaymentData {
    id: number;
    transactionId: string;
    amount: number;
    currency: string;
    created_at: string;
    packageId?: number;
    combinedPackageId?: number;
    shipmentId?: number;
    description?: string;
    shipmentOrderId?: string;
    state: string;
}

interface PaymentHistoryProps {
    payments?: PaymentData[];
    isLoading?: boolean;
    error?: string;
    showViewAll?: boolean;
    onViewAll?: () => void;
    fetchData?: boolean;
    shipmentId?: number;
}

const PaymentHistory = ({
    payments: propPayments,
    isLoading: propIsLoading,
    error: propError,
    showViewAll = true,
    onViewAll,
    fetchData = false,
    shipmentId
}: PaymentHistoryProps) => {
    const [payments, setPayments] = useState<PaymentData[]>(propPayments || []);
    const [isLoading, setIsLoading] = useState<boolean>(propIsLoading || fetchData);
    const [error, setError] = useState<string | null>(propError || null);

    // If fetchData is true, fetch the payments from the API
    useEffect(() => {
        if (fetchData) {
            const fetchPayments = async () => {
                try {
                    setIsLoading(true);
                    const url = shipmentId
                        ? `/api/payments/history?shipmentId=${shipmentId}`
                        : '/api/payments/history';

                    const response = await axios.get(url);
                    if (response.data.success) {
                        setPayments(response.data.data);
                    } else {
                        setError('Failed to fetch payment history');
                    }
                } catch (err) {
                    setError('An error occurred while fetching payment history');
                    console.error('Error fetching payment history:', err);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchPayments();
        }
    }, [fetchData, shipmentId]);

    // Use the prop payments if provided, otherwise use the fetched payments
    const displayPayments = propPayments || payments;
    const totalPayments = useMemo(() => displayPayments.length, [displayPayments]);

    if (isLoading) {
        return (
            <Center py="xl">
                <Loader size="md" />
            </Center>
        );
    }

    if (error) {
        return (
            <Center py="xl">
                <Text color="red">{error}</Text>
            </Center>
        );
    }

    if (displayPayments.length === 0) {
        return (
            <>
                <Flex justify="space-between" align="center" mb="md">
                    <Text fw={600} size="lg">Payment History</Text>
                    <Badge color="gray" size="lg">0 Payments</Badge>
                </Flex>
                <Divider mb="lg" />
                <Paper withBorder radius="md" mb="xl" p="xl">
                    <Center>
                        <Text color="dimmed">No payment records found</Text>
                    </Center>
                </Paper>
            </>
        );
    }

    return (
        <>
            <Flex justify="space-between" align="center" mb="md">
                <Text fw={600} size="lg">Payment History</Text>
                <Badge color="teal" size="lg">{totalPayments} Payments</Badge>
            </Flex>
            <Divider mb="lg" />

            <Paper withBorder radius="md" mb="xl">
                {displayPayments.map((payment, index) => (
                    <div key={`payment-${payment.id}`}>
                        <PaymentHistoryItem {...payment} />
                        {index < displayPayments.length - 1 && <Divider />}
                    </div>
                ))}

                {showViewAll && displayPayments.length > 5 && (
                    <Flex p="md" justify="center">
                        <Button variant="subtle" color="gray" onClick={onViewAll}>
                            View All Transactions
                        </Button>
                    </Flex>
                )}
            </Paper>
        </>
    );
};

export default PaymentHistory;
